import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Linkedin, Mail } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { getUploadUrl } from '../services/api';

export const Team = () => {
  const { t } = useTranslation();
  const [team, setTeam] = useState([]);

  useEffect(() => {
    loadTeam();
  }, []);

  const loadTeam = async () => {
    try {
      const { teamAPI } = await import('../services/api');
      const response = await teamAPI.getAll();
      setTeam(response.data);
    } catch (error) {
      console.error('Error loading team:', error);
      // Fallback to mock data if API fails
      setTeam([
        {
          id: 1,
          name: 'Dr. Marco Rossi',
          role: 'Chief Executive Officer',
          image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400',
          linkedin: '#',
          email: 'marco.rossi@gmed.it',
        },
        {
          id: 2,
          name: 'Dott.ssa Laura Bianchi',
          role: 'Chief Medical Officer',
          image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400',
          linkedin: '#',
          email: 'laura.bianchi@gmed.it',
        },
        {
          id: 3,
          name: 'Ing. Giuseppe Verdi',
          role: 'Head of Engineering',
          image: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=400',
          linkedin: '#',
          email: 'giuseppe.verdi@gmed.it',
        },
        {
          id: 4,
          name: 'Dott.ssa Sofia Romano',
          role: 'Quality Assurance Director',
          image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400',
          linkedin: '#',
          email: 'sofia.romano@gmed.it',
        },
      ]);
    }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-dark overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/4 right-1/3 w-96 h-96 bg-accent rounded-full blur-3xl animate-float"></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="font-display font-bold text-5xl lg:text-6xl mb-6 text-white">
              {t('team.title')}
            </h1>
            <p className="text-xl text-gray-300">
              {t('team.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Team Grid */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={member.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="glass-card overflow-hidden group">
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={getUploadUrl(member.image)}
                      alt={member.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <CardContent className="pt-6 text-center">
                    <h3 className="font-display font-semibold text-xl mb-1 text-foreground">
                      {member.name}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">{member.role}</p>
                    <div className="flex justify-center space-x-2">
                      <Button variant="ghost" size="icon" asChild>
                        <a href={member.linkedin} target="_blank" rel="noopener noreferrer">
                          <Linkedin className="w-4 h-4" />
                        </a>
                      </Button>
                      <Button variant="ghost" size="icon" asChild>
                        <a href={`mailto:${member.email}`}>
                          <Mail className="w-4 h-4" />
                        </a>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Team;